// Components/Gallery.js
import React, { useState, useEffect } from 'react';
import api, { resolveAssetUrl } from '../api';
import './Gallery.css';
import TopBar from './TopBar';
import Footer from './Footer';
import Navbar from './Navbar';

const Gallery = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState(['All']);
  const [modalItem, setModalItem] = useState(null);

  useEffect(() => {
    fetchActiveGalleries();
  }, []);

  const fetchActiveGalleries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/galleries');
      setGalleries(response.data);

      // Extract unique categories
      const cats = ['All', ...new Set(response.data.map(g => g.category).filter(Boolean))];
      setCategories(cats);

      setError(null);
    } catch (err) {
      setError('Failed to load gallery');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredGalleries =
    selectedCategory === 'All' ? galleries : galleries.filter(g => g.category === selectedCategory);

  const totalItems = galleries.length;
  const visibleItems = filteredGalleries.length;

  return (
    <>
      <TopBar />
      <main className="page-gallery">
        <div className="container">
          <Navbar />

          <section className="gallery-section">
            <div className="gallery-container">
              <section className="gallery-hero">
                <div className="gallery-hero-badge">Portfolio</div>
                <h1 className="gallery-title">
                  Our <span>Gallery</span>
                </h1>
                <p className="gallery-subtitle">
                  A curated showcase of our professional work, events, and client engagement moments.
                </p>

                <div className="gallery-metrics">
                  <div className="gallery-metric-item">
                    <strong>{totalItems}</strong>
                    <span>Total Items</span>
                  </div>
                  <div className="gallery-metric-item">
                    <strong>{visibleItems}</strong>
                    <span>Visible</span>
                  </div>
                  <div className="gallery-metric-item">
                    <strong>{selectedCategory}</strong>
                    <span>Current Category</span>
                  </div>
                </div>
              </section>

              <section className="gallery-toolbar">
                <div className="gallery-filters">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      className={`filter-btn ${selectedCategory === cat ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </section>

              {loading ? (
                <div className="gallery-state-card gallery-loading">Loading gallery...</div>
              ) : error ? (
                <div className="gallery-state-card gallery-error">{error}</div>
              ) : (
                <section className="gallery-grid-wrap">
                  <div className="gallery-grid">
                    {filteredGalleries.length > 0 ? (
                      filteredGalleries.map(item => (
                        <article
                          key={item.id}
                          className="gallery-card"
                          onClick={() => setModalItem(item)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => e.key === 'Enter' && setModalItem(item)}
                        >
                          <div className="gallery-image-wrapper">
                            <img
                              src={resolveAssetUrl(item.imageUrl || item.image)}
                              alt={item.title}
                              className="gallery-image"
                            />
                            <div className="gallery-overlay">
                              <span className="overlay-zoom">Quick view</span>
                            </div>
                          </div>
                          <div className="gallery-card-info">
                            <h4>{item.title}</h4>
                            {item.description && <p>{item.description}</p>}
                            {item.category && <span className="category-badge">{item.category}</span>}
                          </div>
                        </article>
                      ))
                    ) : (
                      <div className="gallery-state-card no-gallery">
                        No images found in this category.
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Lightbox Modal */}
              {modalItem && (
                <div className="gallery-modal-backdrop" onClick={() => setModalItem(null)}>
                  <div className="gallery-modal" onClick={e => e.stopPropagation()}>
                    <button className="gallery-modal-close" onClick={() => setModalItem(null)}>
                      ✕
                    </button>
                    <div className="gallery-modal-img-wrap">
                      <img
                        src={resolveAssetUrl(modalItem.imageUrl || modalItem.image)}
                        alt={modalItem.title}
                        className="gallery-modal-img"
                      />
                    </div>
                    <div className="gallery-modal-body">
                      <h3>{modalItem.title}</h3>
                      {modalItem.description && <p>{modalItem.description}</p>}
                      {modalItem.category && <span className="category-badge">{modalItem.category}</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
        <Footer onQuoteClick={() => (window.location.href = '/register')} />
      </main>
    </>
  );
};

export default Gallery;
